import { getMessageWithPlaceholders } from 'shared-locales';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import { EmptyIndicator, LoadingIndicator, User } from 'components/common';
import {
  Box,
  BoxBackLink,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { CourseProgressStatus } from 'components/common/tasks';
import { ParticipantsStats } from 'components/common/stats';
import {
  CourseTitleWithInfo,
  CourseUserMandatory
} from 'components/courses/common';
import { MyTeamFollowBox, useMyTeamFollowContext, useTeamUserList } from '.';

import type { GetTeamCoursesRes } from 'shared-types/responses';

const MyTeamCoursesCourse = () => {
  const { messages, user } = useReduxState();

  const { queryParams } = useMyTeamFollowContext();

  const courseId = queryParams['courseId'] as string;

  // Fetch only on the course id so changing the user-list filters
  // (page/search/participantsOnly) never refetches — the single-course
  // response already carries every supervised user, so filtering is local.
  const { data, loading } = useApiData<GetTeamCoursesRes>({
    url: `/admin/supervisors/${user.id}/team-courses?courseId=${courseId}`,
    onlyIf: Boolean(user?.id && courseId)
  });

  const course = data?.courses?.[0];

  const { numberOfPages, participantsOnly, users } = useTeamUserList({
    // A participant is a user added to the course. We match on addedUserIds
    // rather than progressDataByUser because the latter now holds an entry for
    // every supervised user (we no longer narrow the fetch).
    isParticipant: (user) => Boolean(course?.addedUserIds?.includes(user.id)),
    supervisedUsers: data?.supervisedUsers || []
  });

  const isMandatoryShown = Boolean(course?.completionEnabled);

  return loading && !course ? (
    <LoadingIndicator />
  ) : (
    <>
      <Box
        title={
          <BoxBackLink
            as="/my-team/follow/courses"
            href="/my-team/follow/courses"
            text={messages.commonAllCourses}
          />
        }
      >
        {course ? (
          <div className="row-sm row-center">
            <div className="col">
              <CourseTitleWithInfo
                course={course}
                linkHref={`/courses/${course.slug}`}
              >
                {getMessageWithPlaceholders({
                  message: messages.courseNumberOfChapters,
                  placeholders: { numberOfChapters: course.numberOfChapters }
                })}
              </CourseTitleWithInfo>
            </div>
            <div className="col-min">
              <ParticipantsStats
                completionEnabled={course.completionEnabled}
                progressData={{
                  ...course.progressData,
                  // participantsOnly narrows the bar max to supervised users
                  // added to the course; completed/started are unchanged.
                  participants: participantsOnly
                    ? (data?.supervisedUsers || []).filter((user) =>
                        course.addedUserIds?.includes(user.id)
                      ).length
                    : course.progressData.participants
                }}
                notStartedLabel={
                  participantsOnly
                    ? messages.tasksUsersNotStartedSingular
                    : messages.commonNotStartedOrNotAdded
                }
              />
            </div>
          </div>
        ) : null}
      </Box>

      <MyTeamFollowBox
        numberOfPages={numberOfPages}
        style={{ marginTop: 20 }}
        title={messages.commonUsers}
      >
        {loading ? (
          <LoadingIndicator />
        ) : users?.length ? (
          <BoxTable>
            <BoxTableHeader hideAtBreakpoint={breakpoints.medium}>
              <BoxTableCell>{messages.commonUser}</BoxTableCell>
              {isMandatoryShown ? (
                <BoxTableCell>{messages.commonMandatory}</BoxTableCell>
              ) : null}
              <BoxTableCell>{messages.commonStatus}</BoxTableCell>
            </BoxTableHeader>

            {users.map((user) => (
              <BoxTableRow key={user.id}>
                <BoxTableCell cellBreakpoint={breakpoints.medium}>
                  <User enableProfileLink={true} user={user} />
                </BoxTableCell>

                {isMandatoryShown ? (
                  <BoxTableCell
                    cellBreakpoint={breakpoints.medium}
                    label={messages.commonMandatory}
                  >
                    {/* users not added to the course have no status */}
                    {course.mandatoryReasonsByUser?.[user.id] ? (
                      <CourseUserMandatory
                        mandatoryReasons={
                          course.mandatoryReasonsByUser[user.id]
                        }
                      />
                    ) : null}
                  </BoxTableCell>
                ) : null}

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  label={messages.commonStatus}
                >
                  {course?.progressData?.progressDataByUser?.[user.id] ? (
                    <CourseProgressStatus
                      course={course}
                      id={`team-course-user-progress-${user.id}`}
                      notStartedLabel={
                        course?.addedUserIds?.includes(user.id)
                          ? messages.commonNotStarted
                          : messages.commonNotAdded
                      }
                      progressData={
                        course?.progressData?.progressDataByUser?.[user.id]
                      }
                    />
                  ) : (
                    <div>{messages.commonNoData}</div>
                  )}
                </BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        ) : (
          <EmptyIndicator icon="user" title={messages.commonNoUsers} />
        )}
      </MyTeamFollowBox>
    </>
  );
};

export default MyTeamCoursesCourse;
